/**
 * Color parsing, validation, and derivation for dynamic tenant theming.
 */

const HEX_SHORT = /^#?[0-9a-fA-F]{3}$/;
const HEX_LONG = /^#?[0-9a-fA-F]{6}$/;

export function isValidHex(color) {
  if (typeof color !== 'string') return false;
  const trimmed = color.trim();
  return HEX_SHORT.test(trimmed) || HEX_LONG.test(trimmed);
}

export function normalizeHex(color, fallback = '#4962e5') {
  if (!isValidHex(color)) return fallback;
  let hex = color.trim().replace('#', '');
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((char) => char + char)
      .join('');
  }
  return `#${hex.toLowerCase()}`;
}

export function hexToRgba(hex, alpha) {
  const normalized = normalizeHex(hex);
  const safeHex = normalized.replace('#', '');
  const intValue = parseInt(safeHex, 16);
  const r = (intValue >> 16) & 255;
  const g = (intValue >> 8) & 255;
  const b = intValue & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function channelToLinear(channel) {
  const normalized = channel / 255;
  return normalized <= 0.03928
    ? normalized / 12.92
    : ((normalized + 0.055) / 1.055) ** 2.4;
}

function getRelativeLuminance(hex) {
  const normalized = normalizeHex(hex).replace('#', '');
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return (
    0.2126 * channelToLinear(r) +
    0.7152 * channelToLinear(g) +
    0.0722 * channelToLinear(b)
  );
}

export function getContrastText(backgroundHex, light = '#ffffff', dark = '#111827') {
  const luminance = getRelativeLuminance(backgroundHex);
  return luminance > 0.179 ? dark : light;
}

function mixHex(baseHex, targetHex, weight) {
  const base = normalizeHex(baseHex).replace('#', '');
  const target = normalizeHex(targetHex).replace('#', '');
  const mix = (start, end) =>
    Math.round(parseInt(start, 16) * (1 - weight) + parseInt(end, 16) * weight)
      .toString(16)
      .padStart(2, '0');

  return `#${mix(base.slice(0, 2), target.slice(0, 2))}${mix(base.slice(2, 4), target.slice(2, 4))}${mix(base.slice(4, 6), target.slice(4, 6))}`;
}

export function derivePrimaryScale(mainHex, mode = 'light') {
  const main = normalizeHex(mainHex);
  const white = '#ffffff';
  const black = '#000000';
  const light = mode === 'dark' ? mixHex(main, white, 0.28) : mixHex(main, white, 0.35);
  const dark = mode === 'dark' ? mixHex(main, black, 0.22) : mixHex(main, black, 0.18);
  const hover = mode === 'dark' ? mixHex(main, white, 0.08) : mixHex(main, black, 0.08);
  const active = mode === 'dark' ? mixHex(main, black, 0.12) : mixHex(main, black, 0.16);

  return {
    main,
    light,
    dark,
    hover,
    active,
    soft: mode === 'dark' ? hexToRgba(main, 0.16) : hexToRgba(main, 0.08),
    muted: mode === 'dark' ? hexToRgba(main, 0.18) : hexToRgba(main, 0.1),
    border: mode === 'dark' ? hexToRgba(light, 0.4) : hexToRgba(main, 0.28),
    contrastText: getContrastText(main),
  };
}

export function sanitizeThemeConfig(themeConfig = {}) {
  const mode = ['light', 'dark', 'system'].includes(themeConfig.mode)
    ? themeConfig.mode
    : 'system';
  const density = ['compact', 'default', 'comfortable'].includes(themeConfig.density)
    ? themeConfig.density
    : 'default';

  return {
    ...themeConfig,
    mode,
    density,
    primary_color: isValidHex(themeConfig.primary_color)
      ? normalizeHex(themeConfig.primary_color)
      : undefined,
    secondary_color: isValidHex(themeConfig.secondary_color)
      ? normalizeHex(themeConfig.secondary_color)
      : undefined,
    background_color: isValidHex(themeConfig.background_color)
      ? normalizeHex(themeConfig.background_color)
      : undefined,
    chat_primary_color: isValidHex(themeConfig.chat_primary_color)
      ? normalizeHex(themeConfig.chat_primary_color)
      : undefined,
    chat_primary_dark_color: isValidHex(themeConfig.chat_primary_dark_color)
      ? normalizeHex(themeConfig.chat_primary_dark_color)
      : undefined,
  };
}
